import { DriveStep, startTour, startTourOnFirstVisit } from "./tour";

// Overlay tutorials for the training pages. Steps are anchored on
// `data-tour="…"` attributes in TrainingEventList.tsx, TrainingEventDetail.tsx
// and TrainingSeriesCreate.tsx; steps for sections that aren't rendered are skipped.

const LIST_STEPS: DriveStep[] = [
  {
    popover: {
      title: "Trainingseinheiten",
      description:
        "Hier werden alle Trainings, Spiele und Veranstaltungen verwaltet. " +
        "Diese Anleitung zeigt, wie Termine angelegt und betreut werden.",
    },
  },
  {
    element: '[data-tour="new-event"]',
    popover: {
      title: "Einzelnen Termin anlegen",
      description:
        "<b>Neu</b> legt einen einzelnen Termin an, z.B. ein Zusatztraining oder ein Spiel.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="new-series"]',
    popover: {
      title: "Trainingsserie anlegen",
      description:
        "<b>Serie erstellen</b> legt wiederkehrende Trainings für einen Zeitraum an, z.B. jeden Di und Do bis Saisonende. " +
        "Alle Personen mit passenden Tags werden dabei automatisch als Teilnehmer eingetragen.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="filter"]',
    popover: {
      title: "Filter",
      description:
        "Standardmäßig wird der aktuelle Monat angezeigt. Über den Filter kannst du Zeitraum, Tags (z.B. deine Mannschaft), " +
        "Typ und Verantwortliche einschränken. Der Tag-Filter bleibt gespeichert.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="stats"]',
    popover: {
      title: "Statistik",
      description:
        "Anwesenheit über alle angezeigten Termine. Im Filter kannst du die Statistik auf eine einzelne Person einschränken.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="event-card"]',
    popover: {
      title: "Termin öffnen",
      description:
        "Tippe auf einen Termin, um Teilnehmer, Anwesenheit und Bewertung zu bearbeiten.<br/><br/>" +
        "Mit der Checkbox links oben kannst du mehrere Termine auswählen und gemeinsam löschen.",
      side: "top",
    },
  },
];

const detailSteps = (isNew: boolean): DriveStep[] => [
  {
    popover: {
      title: "Trainingseinheit",
      description:
        "So wird ein Termin angelegt, Anwesenheit erfasst und die Einheit abgeschlossen.",
    },
  },
  {
    element: '[data-tour="event-form"]',
    popover: {
      title: "1. Termindaten",
      description:
        "<b>Typ</b>: Training, Spiel oder Veranstaltung.<br/>" +
        "<b>Tags</b>: für welche Mannschaft/Gruppe der Termin ist (Pflichtfeld).<br/>" +
        "<b>Trainer / Verantwortlich</b> (Pflichtfeld) und optional <b>weitere Trainer</b>.<br/>" +
        "<b>Datum</b> (Pflichtfeld), <b>Startzeit</b> und <b>Dauer</b>.",
      side: "top",
    },
  },
  {
    element: '[data-tour="actions"]',
    popover: {
      title: "2. Speichern",
      description:
        "<b>Speichern</b> legt den Termin an bzw. übernimmt Änderungen. " +
        "Beim ersten Speichern werden alle Personen mit einem der Tags automatisch als Teilnehmer eingetragen (außer „Inaktiv“) " +
        "und sehen den Termin auf ihrer Startseite zum Zu- oder Absagen.",
      side: "top",
    },
  },
  {
    element: '[data-tour="participants"]',
    popover: {
      title: "3. Teilnehmer & Anwesenheit",
      description:
        "Das Abzeichen zeigt die Rückmeldung der Person (<b>Zugesagt</b>, <b>Abgesagt</b>, <b>Ausstehend</b>).<br/><br/>" +
        "Anwesenheit erfassen: <b>✓</b> anwesend, <b>~</b> entschuldigt, <b>✗</b> unentschuldigt.<br/><br/>" +
        "Unten kannst du einzelne Personen hinzufügen, mit dem Papierkorb entfernen. " +
        "Ausstehende Personen können per E-Mail erinnert werden.",
      side: "top",
    },
  },
  {
    element: '[data-tour="batch-add"]',
    popover: {
      title: "Personen mit passenden Tags",
      description:
        "Alle Personen mit einem der Termin-Tags, die noch nicht eingetragen sind. " +
        "Mit <b>Alle auswählen</b> und <b>hinzufügen</b> trägst du die ganze Mannschaft auf einmal ein.",
      side: "top",
    },
  },
  // Grading/cancelling only applies once the event exists.
  ...(isNew ? [] : [{
    element: '[data-tour="actions"]',
    popover: {
      title: "4. Abschließen",
      description:
        "<b>Einheit bewerten</b> (sobald jemand anwesend ist) schließt die Einheit ab: Anwesenheit ist danach gesperrt, " +
        "und anwesende Teilnehmer können mit <b>A</b>/<b>B</b>/<b>C</b> und einer Notiz bewertet werden.<br/><br/>" +
        "<b>Stornieren</b> sagt den Termin mit optionalem Grund ab – er bleibt sichtbar. " +
        "<b>Löschen</b> entfernt ihn endgültig.",
      side: "top" as const,
    },
  }]),
];

const SERIES_STEPS: DriveStep[] = [
  {
    popover: {
      title: "Trainingsserie erstellen",
      description:
        "Legt auf einmal alle Trainings für einen Zeitraum an – ein Termin pro ausgewähltem Wochentag.",
    },
  },
  {
    element: '[data-tour="series-dates"]',
    popover: {
      title: "1. Zeitraum",
      description: "Erster und letzter Tag der Serie, z.B. Saisonstart bis Winterpause.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="series-weekdays"]',
    popover: {
      title: "2. Wochentage",
      description: "Tippe die Trainingstage an (blau = ausgewählt). Mehrere Tage sind möglich.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="series-time"]',
    popover: {
      title: "3. Uhrzeit & Dauer",
      description: "Gilt für alle Termine der Serie. Einzelne Termine können danach noch geändert werden.",
      side: "bottom",
    },
  },
  {
    element: '[data-tour="series-tags"]',
    popover: {
      title: "4. Tags",
      description:
        "Alle Personen mit einem dieser Tags werden automatisch als Teilnehmer jedes Termins eingetragen " +
        "(außer Personen mit dem Tag <b>Inaktiv</b>).",
      side: "top",
    },
  },
  {
    element: '[data-tour="series-trainer"]',
    popover: {
      title: "5. Trainer",
      description: "Verantwortlicher Trainer (Pflichtfeld) und optional weitere Trainer.",
      side: "top",
    },
  },
  {
    element: '[data-tour="series-submit"]',
    popover: {
      title: "6. Serie erstellen",
      description:
        "Wird aktiv, sobald Zeitraum, Wochentage, Tags und Trainer ausgefüllt sind. " +
        "Danach erscheinen alle Termine in der Liste und können einzeln bearbeitet oder storniert werden.",
      side: "top",
    },
  },
];

export const startTrainingListTour = () => startTour(LIST_STEPS);
export const startTrainingListTourOnFirstVisit = () =>
  startTourOnFirstVisit("trainingListTourSeen", LIST_STEPS);

export const startTrainingDetailTour = (isNew: boolean) => startTour(detailSteps(isNew));
// Separate keys: the tour for a saved event covers participants and grading,
// which the new-event page can't show yet.
export const startTrainingDetailTourOnFirstVisit = (isNew: boolean) =>
  startTourOnFirstVisit(
    isNew ? "trainingDetailTourSeen:new" : "trainingDetailTourSeen:existing",
    detailSteps(isNew),
  );

export const startTrainingSeriesTour = () => startTour(SERIES_STEPS);
export const startTrainingSeriesTourOnFirstVisit = () =>
  startTourOnFirstVisit("trainingSeriesTourSeen", SERIES_STEPS);
